/**
 * Virtual background / video effects for the local camera track (SC-158).
 *
 * Wraps `@livekit/track-processors` so the rest of the app deals in a small
 * `BackgroundEffect` value rather than the library's option unions. Mirrors the
 * audio-denoiser pattern already used for the microphone in
 * `LiveKitRoomConnector`: build a processor and attach it to the camera track.
 *
 * Unlike the denoiser, a failure never falls back to the unprocessed track.
 * Someone who picked a background did so to hide the room behind them, so while
 * any effect is selected the raw camera is neither shown nor published; the
 * camera is held instead and the problem is explained (see
 * {@link BackgroundProblem}). Only an explicit "no effect" brings the raw camera
 * back.
 *
 * Three constraints from the library shape the API here:
 *  - `assetPaths`, `segmenterOptions` and `onFrameProcessed` are read **once at
 *    construction** and cannot be changed afterwards without a restart, so they
 *    are passed to `createBackgroundProcessor` rather than to each switch.
 *  - Switching effects goes through `switchTo` on a processor that was created
 *    in `'disabled'` mode. Repeated `setProcessor`/`stopProcessor` churn causes
 *    the visible grey flash this avoids.
 *  - `imagePath` is a URL **string** which the library loads itself (it sets
 *    `crossOrigin = "Anonymous"` and calls `createImageBitmap`). Callers pass a
 *    URL — including a same-origin `blob:` URL — never a bitmap.
 */
import {
  BackgroundProcessor,
  supportsBackgroundProcessors,
  type BackgroundProcessorWrapper,
  type SwitchBackgroundProcessorOptions,
} from '@livekit/track-processors'

import {
  BACKGROUND_BLUR_RADII,
  DEFAULT_VIRTUAL_BACKGROUND_MODEL,
  FALLBACK_VIRTUAL_BACKGROUND_MODEL,
  getVirtualBackgroundAssetPaths,
  isVirtualBackgroundEnabled,
  publicAssetPath,
  type BackgroundBlurIntensity,
  type VirtualBackgroundModelId,
} from '../config/mediaConfig'

// ---------------------------------------------------------------------------
// Effect model
// ---------------------------------------------------------------------------

/**
 * The user's chosen effect, as persisted and as shown in the picker.
 *
 * Image variants carry an **id, not a URL**: workspace and personal images are
 * served through an authenticated route, so their URLs are short-lived and must
 * be resolved at apply time rather than stored.
 */
export type BackgroundEffect =
  | { kind: 'none' }
  | { kind: 'blur'; intensity: BackgroundBlurIntensity }
  /** A bundled platform default, served as a static same-origin asset. */
  | { kind: 'preset'; id: string }
  /** An admin-managed image belonging to the user's company. */
  | { kind: 'workspace'; backgroundId: string }
  /** One of the user's own uploads. */
  | { kind: 'custom'; backgroundId: string }

export const NO_BACKGROUND_EFFECT: BackgroundEffect = { kind: 'none' }

/**
 * Window-event bridge, matching the existing `livekit-toggle-video` convention.
 * The picker lives in the GameMenu tree while the processor lives in the room
 * connector, and the two have no common React ancestor.
 */
export const LIVEKIT_SET_BACKGROUND_EFFECT_EVENT = 'livekit-set-background-effect'

/**
 * Detail for {@link LIVEKIT_SET_BACKGROUND_EFFECT_EVENT}.
 *
 * `persist` defaults to true. The pre-join dialog sets it to **false** because
 * it has already written the preference itself — it must, since the connector
 * may not be mounted yet — and letting the connector write again would mean two
 * durable round-trips for one click.
 */
export interface SetBackgroundEffectDetail {
  effect: BackgroundEffect
  persist?: boolean
}

/**
 * Ask the room connector to apply `effect`. The only way to send
 * {@link LIVEKIT_SET_BACKGROUND_EFFECT_EVENT}.
 *
 * `persist` is required here, though the event treats a missing one as true:
 * whether the connector should save the choice depends on whether the caller
 * already has (or must not), and a forgotten flag would silently save twice or
 * save a fallback the user never picked.
 */
export function requestBackgroundEffect(effect: BackgroundEffect, persist: boolean): void {
  const detail: SetBackgroundEffectDetail = { effect, persist }
  window.dispatchEvent(new CustomEvent(LIVEKIT_SET_BACKGROUND_EFFECT_EVENT, { detail }))
}
/**
 * Emitted by the connector when the selection changes: a pick that is now the
 * selection, or — `reverted` — the running effect it went back to after a new
 * pick failed.
 */
export const LIVEKIT_BACKGROUND_EFFECT_CHANGED_EVENT = 'livekit-background-effect-changed'

/**
 * Emitted while the segmentation pipeline is being torn down and rebuilt, so the
 * camera toggle can be disabled until it settles.
 *
 * This exists for a specific failure: every camera restart makes the library
 * `destroy()` and re-`init()` the processor, and each `init()` allocates a **new
 * WebGL context**. Toggling faster than the browser reclaims them pushes the page
 * past its context ceiling, at which point the browser evicts the
 * least-recently-used context — in this app, often the Phaser game canvas. Phaser
 * handles the loss and rebuilds if the browser restores the context, but the game
 * is blank until then, and an evicted context is not guaranteed to come back.
 */
export const LIVEKIT_VIDEO_TOGGLE_BUSY_EVENT = 'livekit-video-toggle-busy'

let currentVideoToggleBusy = false

/**
 * The last busy state published. Kept because a camera button can mount during
 * the cooldown (the picture-in-picture window opened mid-rebuild, the call bar
 * after the join), and would otherwise offer "on" until the cooldown ends.
 */
export function readVideoToggleBusy(): boolean {
  return currentVideoToggleBusy
}

export function publishVideoToggleBusy(busy: boolean): void {
  if (busy === currentVideoToggleBusy) return
  currentVideoToggleBusy = busy
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<{ busy: boolean }>(LIVEKIT_VIDEO_TOGGLE_BUSY_EVENT, { detail: { busy } })
  )
}

/** Test seam — the state is page-lifetime. */
export function resetVideoToggleBusyForTests(): void {
  currentVideoToggleBusy = false
}

/**
 * How long the camera toggle stays disabled after a rebuild starts, when no
 * processed frame arrives to tell us the pipeline is live again. Purely a safety
 * net — the normal path clears as soon as the first frame lands.
 */
export const VIDEO_TOGGLE_COOLDOWN_MS = 3000

/**
 * Emitted when a camera start fails because access is blocked, so the toolbar
 * can say so instead of leaving a button that silently fails on every press.
 * Detail `{ blocked: boolean }` — cleared once a camera start succeeds.
 */
export const LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT = 'livekit-camera-permission-blocked'

// ---------------------------------------------------------------------------
// Problems: why the selected effect is not on screen
// ---------------------------------------------------------------------------

/**
 * Why a selected effect is not running as chosen.
 *
 * - `gone`: the image was deleted or is no longer the viewer's (404/403).
 * - `unavailable`: the image could not be fetched (offline, 5xx, timed out).
 * - `processor-failed`: the segmentation pipeline would not start.
 * - `unsupported`: this browser or device cannot run effects at all.
 * - `slow`: the effect runs, but the device cannot keep up with it.
 * - `switch-failed`: a new pick failed while an older effect was live; the old
 *   one stays on.
 */
export type BackgroundProblemCause =
  | 'gone'
  | 'unavailable'
  | 'processor-failed'
  | 'unsupported'
  | 'slow'
  | 'switch-failed'

export interface BackgroundProblem {
  cause: BackgroundProblemCause
  /** The effect the problem is about — the failed pick for `switch-failed`. */
  effect: BackgroundEffect
  /** `switch-failed` only: the effect still in use. */
  stillUsing?: BackgroundEffect
}

/**
 * Whether the camera is kept off because of `problem`.
 *
 * Every cause but two holds it: the raw camera is never the answer to a
 * background that cannot be shown. A slow device keeps the effect running, and
 * a failed switch keeps the previous effect running.
 */
export function backgroundProblemHoldsCamera(problem: BackgroundProblem | null): boolean {
  return problem !== null && problem.cause !== 'slow' && problem.cause !== 'switch-failed'
}

export function backgroundProblemsEqual(
  a: BackgroundProblem | null,
  b: BackgroundProblem | null
): boolean {
  if (a === null || b === null) return a === b
  return (
    a.cause === b.cause &&
    backgroundEffectKey(a.effect) === backgroundEffectKey(b.effect) &&
    (a.stillUsing ? backgroundEffectKey(a.stillUsing) : '') ===
      (b.stillUsing ? backgroundEffectKey(b.stillUsing) : '')
  )
}

/**
 * What the camera background is doing right now, for the surfaces that explain
 * it: the camera button's warning icon and the local self-view.
 *
 * `held`: the camera has been asked for but is deliberately not shown or
 * published while the selected effect gets ready — from the camera-start gate
 * waiting on its processor, through the start itself, to the first composited
 * frame. The self-view covers itself rather than show the frames underneath.
 */
export interface CameraBackgroundState {
  held: boolean
  problem: BackgroundProblem | null
}

/**
 * Whether the camera, if the user wants it on, is off for its background: being
 * readied, or kept off by a problem. The camera button shows it in the warning
 * colour rather than as on, and other participants are told (F8), since to them
 * it is otherwise indistinguishable from a camera turned off.
 */
export function cameraWaitingForBackground(state: CameraBackgroundState): boolean {
  return state.held || backgroundProblemHoldsCamera(state.problem)
}

/**
 * How long a processor may take to start — LiveKit's `setProcessor`, or the
 * preview's `init()` — before it counts as failed. Nothing beneath bounds it:
 * `init()` loads MediaPipe's script, WASM and model with no timeout of its own.
 * Generous, because a cold load is about 18 MB on the wire over whatever link
 * the user has: the WASM compresses to about 3 MB, but the default segmentation
 * model barely compresses (16 MB raw). That is a link of about 5 Mbit/s inside
 * this bound, and the camera is held, not raw, while it waits. The download
 * starts before the clock does ({@link prewarmBackgroundAssets}), at mount for a
 * saved effect and at the first pick.
 */
export const PROCESSOR_START_TIMEOUT_MS = 30_000

export const LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT = 'livekit-camera-background-state'

const IDLE_CAMERA_BACKGROUND_STATE: CameraBackgroundState = { held: false, problem: null }
let currentCameraBackgroundState: CameraBackgroundState = IDLE_CAMERA_BACKGROUND_STATE

/**
 * The last state published. Kept because the toolbar and the video tiles mount
 * after the join, and a problem found during it (a deleted image, a device
 * that cannot run effects) would otherwise never reach them.
 */
export function readCameraBackgroundState(): CameraBackgroundState {
  return currentCameraBackgroundState
}

export function publishCameraBackgroundState(state: CameraBackgroundState): void {
  if (
    state.held === currentCameraBackgroundState.held &&
    backgroundProblemsEqual(state.problem, currentCameraBackgroundState.problem)
  ) {
    return
  }
  currentCameraBackgroundState = state
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<CameraBackgroundState>(LIVEKIT_CAMERA_BACKGROUND_STATE_EVENT, {
      detail: state,
    })
  )
}

/** Test seam — the state is page-lifetime. */
export function resetCameraBackgroundStateForTests(): void {
  currentCameraBackgroundState = IDLE_CAMERA_BACKGROUND_STATE
}

/**
 * The bundled platform presets, shown to every workspace out of the box so the
 * picker is never empty.
 *
 * Generated by `scripts/generate-background-presets.py`, which is also where the
 * design rationale lives — they are deliberately soft and low-detail, because
 * fine detail near the matte boundary is what produces a visible halo around the
 * subject.
 *
 * `id` is persisted, so **renaming one orphans anyone who had it selected**
 * (they fall back to no effect). Add rather than rename.
 */
export interface BackgroundPreset {
  id: string
  label: string
}

export const BACKGROUND_PRESETS: readonly BackgroundPreset[] = [
  { id: 'studio-neutral', label: 'Studio' },
  { id: 'sevv-warm', label: 'Sevv - Warm' },
  { id: 'sevv-slate', label: 'Sevv - Slate' },
  { id: 'openprovider', label: 'Openprovider' },
  { id: 'procys', label: 'Procys' },
] as const

/**
 * Human-readable name for an effect, for buttons and status text.
 *
 * Falls back to a generic label for a preset id we no longer ship, rather than
 * showing the raw id — an orphaned preference should read as "a background",
 * not as internal data.
 *
 * Uploaded images say whose they are rather than "Custom background": it is
 * shorter, which matters where this appears (a narrow settings column beside a
 * clear button), and it distinguishes an image the viewer can remove from one
 * their company manages.
 */
export function describeBackgroundEffect(effect: BackgroundEffect): string {
  switch (effect.kind) {
    case 'none':
      return 'No effect'
    case 'blur':
      return effect.intensity === 'strong' ? 'Blur - strong' : 'Blur - light'
    case 'preset':
      return BACKGROUND_PRESETS.find((preset) => preset.id === effect.id)?.label ?? 'Background'
    case 'workspace':
      return 'Company image'
    case 'custom':
      return 'Your image'
  }
}

/** Effect kinds that need an image URL resolved before they can be applied. */
export function requiresBackgroundImage(effect: BackgroundEffect): boolean {
  return effect.kind === 'preset' || effect.kind === 'workspace' || effect.kind === 'custom'
}

/**
 * Warm the browser cache for every bundled plate.
 *
 * Switching to an image goes through the library's `update()`, which clears the
 * current background *before* awaiting the new image — so however long that load
 * takes is a gap with no background at all. With the file already cached the gap
 * is a frame or two; without it, it is a visible flash.
 *
 * `crossOrigin` matters: the library sets it when it loads the image, and a
 * response cached without the CORS flavour is not always reused for a CORS
 * request. Preloading the same way keeps it to one fetch.
 */
let presetImagesPreloaded = false

export function preloadBackgroundPresetImages(): void {
  if (presetImagesPreloaded) return
  if (typeof Image === 'undefined') return
  presetImagesPreloaded = true

  for (const preset of BACKGROUND_PRESETS) {
    const url = resolveStaticBackgroundImageUrl({ kind: 'preset', id: preset.id })
    if (!url) continue
    const image = new Image()
    image.crossOrigin = 'Anonymous'
    image.src = url
  }
}

/** Test seam — the preload is a once-per-page side effect. */
export function resetBackgroundPresetPreloadForTests(): void {
  presetImagesPreloaded = false
}

/**
 * Load and decode `url` the way the processor will, before asking it to switch.
 *
 * The library's `update()` drops the current background the moment it is asked
 * to switch and only then loads the new image, so the time that load takes is
 * time with no background behind the user. With the image already decoded in
 * the browser's memory cache that window shrinks to the GPU upload, and it never
 * includes a download. Rejects if the image will not load, which is the
 * earliest point a broken image can be caught while the old effect is still
 * live.
 */
export async function preloadBackgroundImage(url: string): Promise<void> {
  if (typeof Image === 'undefined') return
  const image = new Image()
  // As the library sets it; a response cached without CORS is not reused for a
  // CORS request, and the switch would download it again.
  image.crossOrigin = 'Anonymous'
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = () => reject(new Error(`background image did not load: ${url}`))
    image.src = url
  })
  if (typeof image.decode === 'function') {
    await image.decode().catch(() => undefined)
  }
}

/** Stable key for caching resolved image URLs and for analytics. */
export function backgroundEffectKey(effect: BackgroundEffect): string {
  switch (effect.kind) {
    case 'none':
      return 'none'
    case 'blur':
      return `blur:${effect.intensity}`
    case 'preset':
      return `preset:${effect.id}`
    case 'workspace':
      return `workspace:${effect.backgroundId}`
    case 'custom':
      return `custom:${effect.backgroundId}`
  }
}

export function backgroundEffectsEqual(a: BackgroundEffect, b: BackgroundEffect): boolean {
  return backgroundEffectKey(a) === backgroundEffectKey(b)
}

// ---------------------------------------------------------------------------
// Capability + availability gates
// ---------------------------------------------------------------------------

/**
 * This probe is answered **once per page and cached**, and that is not an
 * optimisation.
 *
 * `supportsBackgroundProcessors()` ends in
 * `document.createElement('canvas').getContext('webgl2')` — it allocates a real
 * WebGL context on every call and never releases it. A browser allows only a
 * handful per page (Chrome around sixteen) and evicts the least-recently-used
 * one when that ceiling is passed. In this app that is often the Phaser game
 * canvas, which stays blank until the browser restores the context — if it ever
 * does.
 *
 * Called from six places, including every effect apply, every preview attach and
 * every render of the company settings tab, that ceiling is reached in an
 * ordinary session. Caching is what keeps it to one.
 *
 * Neither answer can change within a session, so a cached value is also simply
 * correct.
 */
let cachedProcessingSupported: boolean | null = null

/**
 * Whether this browser can run background segmentation at all.
 * Independent of the rollout flag — a device that cannot run the processor must
 * not be offered the control even where the feature is switched on.
 */
export function isBackgroundProcessingSupported(): boolean {
  if (cachedProcessingSupported !== null) return cachedProcessingSupported
  try {
    cachedProcessingSupported = supportsBackgroundProcessors()
  } catch {
    // The check touches WebGL/`createImageBitmap`; treat a throw as unsupported
    // rather than letting it escape into a render path.
    cachedProcessingSupported = false
  }
  return cachedProcessingSupported
}

/** Test seam — the probe is cached for the life of the page. */
export function resetBackgroundCapabilityCacheForTests(): void {
  cachedProcessingSupported = null
}

/** Both gates: the deployment has the feature on **and** the device can run it. */
export function isBackgroundEffectAvailable(): boolean {
  return isVirtualBackgroundEnabled() && isBackgroundProcessingSupported()
}

/**
 * Whether `effect` will actually run here: one is chosen **and** this
 * deployment and device can run it.
 *
 * "Chosen" alone is not enough. The preference is durable and cross-device, so
 * it reaches browsers that cannot run segmentation and deployments with the flag
 * off — and there the picker is hidden, so the user cannot clear it. Anything
 * that costs something because an effect is on (the 720p capture, the camera
 * toggle cooldown) must ask this, or those users pay for an effect they never
 * get and cannot turn off.
 */
export function isBackgroundEffectActive(effect: BackgroundEffect): boolean {
  return effect.kind !== 'none' && isBackgroundEffectAvailable()
}

// ---------------------------------------------------------------------------
// Effect → processor options
// ---------------------------------------------------------------------------

const DISABLED_OPTIONS: SwitchBackgroundProcessorOptions = { mode: 'disabled' }

/**
 * Map an effect to the library's switch options.
 *
 * `resolvedImageUrl` must be supplied for the image kinds. When it is missing
 * this returns `disabled` — the library's pass-through, which is the **raw
 * camera** — rather than throwing. So for anything but "none", `disabled` means
 * "cannot be applied", and callers must treat it as a failure to hold the camera
 * for, never as something to put on a track.
 */
export function toProcessorOptions(
  effect: BackgroundEffect,
  resolvedImageUrl?: string | null
): SwitchBackgroundProcessorOptions {
  switch (effect.kind) {
    case 'none':
      return DISABLED_OPTIONS
    case 'blur':
      return {
        mode: 'background-blur',
        blurRadius: BACKGROUND_BLUR_RADII[effect.intensity],
      }
    case 'preset':
    case 'workspace':
    case 'custom':
      if (!resolvedImageUrl) return DISABLED_OPTIONS
      return { mode: 'virtual-background', imagePath: resolvedImageUrl }
  }
}

/**
 * Resolve the image URL for an effect, or `null` when none is available.
 *
 * Presets are bundled static assets and resolve synchronously. Workspace and
 * personal images are **not** resolvable here — they are served through an
 * authenticated route and must be fetched to a `blob:` URL by the caller, which
 * is why this returns `null` for them rather than guessing a path; see
 * {@link resolveBackgroundImage}.
 */
export function resolveStaticBackgroundImageUrl(effect: BackgroundEffect): string | null {
  if (effect.kind !== 'preset') return null
  return publicAssetPath(`backgrounds/${effect.id}.jpg`)
}


/**
 * Resolve an effect's image, saying whether a failure means the image is *gone*.
 *
 * `revalidate` must be true wherever the result is about to be applied. The
 * library caches object URLs for the life of the page, so without it a
 * background deleted by an admin keeps resolving from cache and survives every
 * rejoin short of a full reload.
 */
export async function resolveBackgroundImage(
  effect: BackgroundEffect,
  options: { revalidate?: boolean } = {}
): Promise<{ url: string | null; gone: boolean }> {
  if (effect.kind === 'preset') {
    return { url: resolveStaticBackgroundImageUrl(effect), gone: false }
  }
  if (effect.kind === 'workspace' || effect.kind === 'custom') {
    const { lookupBackgroundImage } = await import('./backgroundLibrary')
    const result = await lookupBackgroundImage(effect.backgroundId, options)
    if (result.status === 'ok') return { url: result.url, gone: false }
    return { url: null, gone: result.status === 'gone' }
  }
  return { url: null, gone: false }
}

// ---------------------------------------------------------------------------
// Processor construction
// ---------------------------------------------------------------------------

/**
 * Per-frame timings reported by the processor.
 *
 * `filterTimeMs` is the whole per-frame cost: the library starts its clock before
 * it hands the frame to the segmenter, whose callback runs synchronously, so the
 * segmentation is already inside it. `processingTimeMs` is the library's
 * `segmentationTimeMs + filterTimeMs`, which counts the segmentation twice: it is
 * kept for callers that read it, but health is judged on {@link frameCostMs}.
 * `filterTimeMs` is optional because the monitor must keep working on a stats
 * object that lacks it.
 */
export interface BackgroundFrameStats {
  processingTimeMs: number
  segmentationTimeMs?: number
  filterTimeMs?: number
}

/**
 * What one frame cost the pipeline: `filterTimeMs` when the processor reported a
 * usable one, else `processingTimeMs`. The fallback overstates the cost (it adds
 * the segmentation a second time), which errs towards stepping down, never away
 * from it; the library reports `filterTimeMs` on every path, including the
 * Safari and Firefox canvas loop, so this only matters for stats from elsewhere.
 */
export function frameCostMs(stats: BackgroundFrameStats): number {
  const { filterTimeMs } = stats
  return typeof filterTimeMs === 'number' && Number.isFinite(filterTimeMs)
    ? filterTimeMs
    : stats.processingTimeMs
}

export interface CreateBackgroundProcessorOptions {
  /** Called for every processed frame; drives the degradation ladder. */
  onFrameProcessed?: (stats: BackgroundFrameStats) => void
  /**
   * Mode to construct in. Defaults to `disabled`.
   *
   * Pass the **target** mode whenever it is already known, because the library
   * only loads a background image inside `init()` when `imagePath` was present
   * at construction. Constructing disabled and then calling `switchTo` defers
   * that load until after the processor is attached — and every frame in
   * between is passed through raw, which is the visible "camera first, then the
   * background" flash on page load.
   */
  initial?: SwitchBackgroundProcessorOptions
}

/**
 * Whether this page has stepped down from the default segmentation model.
 *
 * One-way and per page, on purpose. The default model is the heavier one, and a
 * device that cannot keep up with it gets the lighter one for the rest of the
 * page: the next processor (a camera restart, the call after the preview, an
 * effect switched off and on) is built with it from the start. Trying the heavy
 * model again whenever a processor is rebuilt would make a slow device pay for
 * the slow verdict, and the camera restart that follows it, over and over. A
 * reload gets a fresh try, which is the right moment: the machine may not be
 * the same.
 */
let segmentationSteppedDown = false

/** The model processors are built with now. */
export function activeSegmentationModel(): VirtualBackgroundModelId {
  return segmentationSteppedDown
    ? FALLBACK_VIRTUAL_BACKGROUND_MODEL
    : DEFAULT_VIRTUAL_BACKGROUND_MODEL
}

/**
 * Step down to the fallback model, once per page.
 *
 * True only for the call that does it, which is how a caller knows a slow
 * verdict is answered by rebuilding rather than by the "slow" notice. False when
 * the page has stepped down already (the lighter model is slow too: the notice
 * is the answer) or when the default is the fallback.
 */
export function stepDownSegmentation(): boolean {
  if (segmentationSteppedDown) return false
  if (DEFAULT_VIRTUAL_BACKGROUND_MODEL === FALLBACK_VIRTUAL_BACKGROUND_MODEL) return false
  segmentationSteppedDown = true
  return true
}

/** Test seam: the step-down lasts for the page. */
export function resetSegmentationStepDownForTests(): void {
  segmentationSteppedDown = false
}

/**
 * The model each processor was built with. The model is read once at
 * construction, so a processor cannot be told to change it; this is how a caller
 * finds the ones that must be rebuilt.
 */
const processorModels = new WeakMap<object, VirtualBackgroundModelId>()

/**
 * Whether `processor` runs a model the page has since stepped down from, so it
 * must be replaced by a new one. False for a processor this module did not build.
 */
export function isProcessorOnSteppedDownModel(processor: object | null | undefined): boolean {
  if (!processor) return false
  const model = processorModels.get(processor)
  return model !== undefined && model !== activeSegmentationModel()
}

/**
 * Build a processor wired to our self-hosted MediaPipe assets. Returns `null`
 * when the device cannot support one, so callers get a single falsy check
 * instead of a throw on unsupported hardware.
 */
export function createBackgroundProcessor(
  options: CreateBackgroundProcessorOptions = {}
): BackgroundProcessorWrapper | null {
  if (!isBackgroundProcessingSupported()) return null

  const model = activeSegmentationModel()
  const processor = BackgroundProcessor({
    ...(options.initial ?? DISABLED_OPTIONS),
    // Without these the library reaches out to jsDelivr and
    // storage.googleapis.com at runtime; see mediaConfig for provenance. The
    // model and how its output becomes the mask travel together in these paths.
    assetPaths: getVirtualBackgroundAssetPaths(model),
    ...(options.onFrameProcessed ? { onFrameProcessed: options.onFrameProcessed } : {}),
  })

  if (processor && typeof processor === 'object') processorModels.set(processor, model)
  suppressRawFirstFrame(processor)
  return processor
}

/**
 * Stop the library emitting one **unprocessed** frame every time a processor starts.
 *
 * `BackgroundTransformer.transform()` opens with:
 *
 * ```js
 * if (this.isFirstFrame) {
 *   controller.enqueue(frame.clone())   // the raw camera, straight out
 *   await <until the input <video> has painted>
 * }
 * this.isFirstFrame = false
 * ```
 *
 * So the first frame out of a fresh processor is the user's real room — held on
 * screen for that awaited delay plus the first segmentation, which is long enough
 * to read as "my room, then the background". `destroy()` sets the flag back to
 * true, so it is not once per page: it is **once per camera start**, and it goes
 * to the SFU as well as to the local tile, meaning other participants see it too.
 *
 * No amount of capture-time attachment prevents this — the processor can be
 * attached inside `createLocalTracks`, before publish, and still emit this frame.
 *
 * Dropping the warm-up is safe here: segmentation reads the `VideoFrame`
 * (`segmentForVideo(frame, …)`) and rendering reads it too
 * (`drawFrame(frame)` → `gl.renderFrame(frame)`). `inputVideo` is used only for
 * sizing and for that awaited delay, so the composited path needs nothing the
 * warm-up provides. The cost is that segmentation runs on the very first frame
 * without the element having painted, which at worst makes one frame's mask
 * slightly rougher — against showing the room the user asked to hide.
 *
 * Written defensively because it reaches past the public API: if a future version
 * renames the field or drops the passthrough, this silently does nothing rather
 * than throwing. `libraryStillPassesFirstFrameThrough()` is the paired guard —
 * it fails the build when the workaround is no longer needed, so this does not
 * outlive its reason.
 */
function suppressRawFirstFrame(processor: BackgroundProcessorWrapper | null): boolean {
  const transformer = (processor as { transformer?: { isFirstFrame?: unknown } } | null)
    ?.transformer
  if (!transformer || typeof transformer.isFirstFrame !== 'boolean') return false
  ;(transformer as { isFirstFrame: boolean }).isFirstFrame = false
  return true
}

/**
 * Whether `processor` will emit a raw frame before its first composited one.
 *
 * Exported for tests: it is the only way to assert the suppression actually took,
 * since the flag lives on a library internal.
 */
export function willPassFirstFrameThrough(processor: BackgroundProcessorWrapper | null): boolean {
  const transformer = (processor as { transformer?: { isFirstFrame?: unknown } } | null)
    ?.transformer
  return transformer?.isFirstFrame === true
}

/**
 * Warm the HTTP cache for everything `init()` will need.
 *
 * `init()` cannot start until a camera track exists, and it then fetches the
 * MediaPipe WASM fileset and the segmentation model (the default one is about
 * 16 MB) before it can produce a single processed frame. On a cold cache that is
 * the bulk of the delay during which the user's real room is on screen — and on
 * the wire.
 *
 * Kicking the fetches off at mount (for a saved effect) or at the first pick
 * overlaps them with room connection, or with the pick's own work, instead, and
 * starts them before the timed `init()` does. Deliberately a no-op when no
 * effect is selected: pulling about 18 MB for a feature the user is not using
 * would be a poor trade. Once per page, whichever comes first.
 */
let backgroundAssetsPrewarmed = false

export function prewarmBackgroundAssets(effect: BackgroundEffect): void {
  if (backgroundAssetsPrewarmed) return
  if (backgroundEffectsEqual(effect, NO_BACKGROUND_EFFECT)) return
  if (!isBackgroundEffectAvailable()) return
  backgroundAssetsPrewarmed = true

  const { tasksVisionFileSet, modelAssetPath } =
    getVirtualBackgroundAssetPaths(activeSegmentationModel())
  const urls = [
    modelAssetPath,
    // FilesetResolver picks SIMD or no-SIMD at runtime; warming the SIMD pair
    // covers every browser we support background effects on.
    `${tasksVisionFileSet}/vision_wasm_internal.js`,
    `${tasksVisionFileSet}/vision_wasm_internal.wasm`,
  ]
  const imageUrl = resolveStaticBackgroundImageUrl(effect)
  if (imageUrl) urls.push(imageUrl)

  warmHttpCache(urls)
}

/** Fire and forget: nothing waits on a prewarm, least of all a frame. */
function warmHttpCache(urls: readonly string[]): void {
  for (const url of urls) {
    // Best-effort: a failure here costs nothing beyond a slower first apply.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    void fetch(url, { cache: 'force-cache' }).catch(() => {})
  }
}

let fallbackModelPrewarmed = false

/**
 * Warm the HTTP cache for the model a busy machine steps down to.
 *
 * The step-down builds a new processor, and its `init()` fetches the model
 * before it can produce a frame; the old processor keeps the tile until then, but
 * a cold fetch (or one that fails) leaves a busy machine on the heavier model and
 * the camera blinking for longer. Called when a busy streak begins, a few seconds
 * before the step-down would need it, so the rebuild reads from cache. Only the
 * model: the WASM fileset is the same for both and warmed with the first effect.
 *
 * Once per page, and nothing for a machine that never gets busy: that is why this
 * waits for a streak and does not ride on {@link prewarmBackgroundAssets}.
 */
export function prewarmFallbackSegmentationModel(): void {
  if (fallbackModelPrewarmed) return
  // Already on it (the rebuild fetches it itself), or nothing to step down to.
  if (segmentationSteppedDown) return
  if (DEFAULT_VIRTUAL_BACKGROUND_MODEL === FALLBACK_VIRTUAL_BACKGROUND_MODEL) return
  if (!isBackgroundEffectAvailable()) return
  fallbackModelPrewarmed = true
  warmHttpCache([getVirtualBackgroundAssetPaths(FALLBACK_VIRTUAL_BACKGROUND_MODEL).modelAssetPath])
}

/** Test seam — the prewarms are once-per-page side effects. */
export function resetBackgroundAssetPrewarmForTests(): void {
  backgroundAssetsPrewarmed = false
  fallbackModelPrewarmed = false
}

/**
 * Force a canvas to give up its WebGL context.
 *
 * The processor library's `cleanup()` deletes its programs, textures and
 * framebuffers but never calls `loseContext()` — `WEBGL_lose_context` does not
 * appear anywhere in the package. So a destroyed processor's context survives
 * until its canvas is garbage collected, which is not deterministic and can lag
 * well behind.
 *
 * That matters because contexts are a capped, page-wide resource: pass the
 * ceiling and the browser evicts the least-recently-used one, which in this app
 * is often the Phaser game canvas — blank until the browser restores it, if it
 * does. Every camera restart, device switch and join rebuilds a processor, so
 * "eventually" is not good enough.
 */
function releaseWebGLContext(canvas: OffscreenCanvas | HTMLCanvasElement | undefined): void {
  if (!canvas) return
  try {
    const gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
  } catch {
    // Nothing to do — a canvas that will not hand back its context is one the
    // browser is already finished with.
  }
}

/**
 * Destroy a processor **and** release the WebGL context it was using.
 *
 * Always prefer this to calling `processor.destroy()` directly; see
 * {@link releaseWebGLContext} for why destroy alone is not enough.
 */
export async function destroyBackgroundProcessor(
  processor: BackgroundProcessorWrapper | null | undefined
): Promise<void> {
  if (!processor) return
  // Captured first: destroy clears the transformer's reference to it.
  const canvases = [processor.canvas, processor.displayCanvas]
  try {
    await processor.destroy()
  } catch {
    // Still release the contexts below — a failed destroy is exactly the case
    // where a leaked context is most likely.
  }
  for (const canvas of canvases) releaseWebGLContext(canvas)
}

// ---------------------------------------------------------------------------
// Runtime health → graduated degradation
// ---------------------------------------------------------------------------

/**
 * What to do about slow frames.
 *
 * `disable` is the verdict that matters on its own: the first one on a page steps
 * down to the lighter segmentation model ({@link stepDownSegmentation}),
 * silently; one after that raises a one-time `slow` warning, and never turns the
 * effect off. `reduce-quality` is not acted on by one reading, but one that
 * *lasts* ({@link createSlowStreakTracker}) takes the same silent step-down, and
 * never a notice: a machine that is merely busy is eased off, not told it is
 * failing. No caller lowers the capture (SC-377, SC-478): the patched library
 * follows a frame-size change, but each resize clears its mask to background, so
 * the person is gone until the next segmentation lands.
 */
export type BackgroundHealthAction = 'ok' | 'reduce-quality' | 'disable'

export interface BackgroundHealthThresholds {
  /** Frame budget in ms before a frame counts as slow. */
  slowFrameMs: number
  /** Frame budget in ms before a frame counts as severely slow. */
  severeFrameMs: number
  /** How many recent frames to consider. */
  windowSize: number
  /** Fraction of the window that must be slow before acting. */
  slowFrameRatio: number
  /**
   * Frames after a processor starts that never count towards a sustained
   * `reduce-quality`: shader compilation, WASM warm-up and the first texture
   * uploads make them slow on every machine.
   */
  warmupFrames: number
  /** How long `reduce-quality` must hold without a break before it counts as sustained. */
  sustainedMs: number
}

/**
 * Re-derived for the effect capture, 1280x720 at 24 fps (SC-478): 41.7 ms
 * between frames, where the numbers they replace (12 and 28 ms) were set for
 * 640x360 at 30 fps, 33.3 ms between frames.
 *
 * `slowFrameMs` keeps its share of the interval, since that is what says "keeping
 * up": 12/33.3 is 36%, so it is 15 ms (36% of 41.7). Below 15 ms a frame leaves
 * nearly two thirds of its interval idle. Holding the share rather than the
 * milliseconds is deliberate: the frame interval grew by a quarter, so keeping
 * 12 ms would call a machine slow that uses the same share as before.
 *
 * `severeFrameMs` is not the same share (84% would be 35 ms) but is held under
 * the *shortest* interval it can meet. The frame rate is only an `ideal`: a
 * camera that stays at 30 fps (one that ignores it, or has no 24 fps mode)
 * delivers a frame every 33.3 ms, and a pipeline whose frames take 34 ms is then
 * falling behind the camera with every frame, which a budget of 35 ms never
 * calls severe, so the "slow" notice would be missed on the lighter model. 32 ms is
 * 77% of the 24 fps interval and 96% of the 30 fps one: a frame that costs that
 * much leaves next to nothing idle at either rate.
 *
 * Step-down on a sustained `reduce-quality` needs half of the last 60 frames
 * (2.5 s at 24 fps) at 15 ms or more, with no break, for 5 s, and only after the
 * first 24 frames (1 s) of a processor have been set aside, so the earliest it
 * can happen is about 8.5 s after a start. A comfortable machine does not come
 * near it: it would need the typical frame to cost a third of the interval,
 * sustained, not a spike (a garbage collection or a tab switch is a few frames,
 * and a break of one second between frames restarts the count).
 *
 * What the milliseconds measure: the **true cost of one frame**, `filterTimeMs`
 * (see {@link frameCostMs}): segmentation, mask update and compositing, from the
 * frame arriving to the processed frame leaving. The library's own
 * `processingTimeMs` adds the segmentation a second time, which overstates the
 * cost by up to a factor of two (when segmentation dominates the frame), so the
 * same numbers judged on it called a machine busy at about half the real work.
 * On the true cost they call busy only a machine that spends over a third of the
 * interval on each frame.
 *
 * These are derived, not measured: the before/after run on the local stack
 * (SC-478, criterion 7) is what confirms them on real hardware.
 */
export const DEFAULT_BACKGROUND_HEALTH_THRESHOLDS: BackgroundHealthThresholds = {
  slowFrameMs: 15,
  severeFrameMs: 32,
  windowSize: 60,
  slowFrameRatio: 0.5,
  warmupFrames: 24,
  sustainedMs: 5_000,
}

/**
 * Decide, from a rolling window of frame timings, whether to step down.
 *
 * Kept pure and separate from the processor so the policy can be tested without
 * a camera, a GPU, or a LiveKit room.
 */
export function evaluateBackgroundHealth(
  recentFrames: readonly BackgroundFrameStats[],
  thresholds: BackgroundHealthThresholds = DEFAULT_BACKGROUND_HEALTH_THRESHOLDS
): BackgroundHealthAction {
  // Don't act on a partial window: early frames include warm-up costs and would
  // otherwise trigger a step-down on every single apply.
  if (recentFrames.length < thresholds.windowSize) return 'ok'

  const window = recentFrames.slice(-thresholds.windowSize)
  let slow = 0
  let severe = 0

  for (const frame of window) {
    const cost = frameCostMs(frame)
    if (cost >= thresholds.severeFrameMs) severe += 1
    else if (cost >= thresholds.slowFrameMs) slow += 1
  }

  const threshold = thresholds.slowFrameRatio * window.length
  if (severe >= threshold) return 'disable'
  if (severe + slow >= threshold) return 'reduce-quality'
  return 'ok'
}

/**
 * A break between two frames longer than this ends a slow streak. A muted camera,
 * a hidden tab or a stalled capture produces none, and the wall clock moving on
 * meanwhile says nothing about how heavy the pipeline is.
 */
const SLOW_STREAK_MAX_GAP_MS = 1_000

export interface SlowStreakTracker {
  /**
   * Feed the verdict for the frame just recorded. True once, when the verdict
   * has been something other than `ok` for {@link BackgroundHealthThresholds.sustainedMs}.
   */
  observe: (action: BackgroundHealthAction) => boolean
  /**
   * Whether a streak is running as of the last frame: a verdict other than `ok`
   * since the warm-up, with no healthy frame or stall in between. It is how a
   * caller tells that a step-down may be coming, and can get ready for it.
   */
  readonly streaking: boolean
  /** Start again, for a new processor or an effect the old samples do not describe. */
  reset: () => void
}

/**
 * Tells a machine that is busy for a moment from one that is busy for good.
 *
 * `evaluateBackgroundHealth` judges a window and has no memory, so a reading that
 * `reduce-quality` repeats for as long as the pipeline sits near saturation looked
 * the same as one passing blip, and was ignored. This keeps the memory: wall-clock
 * time, not a frame count, because the capture rate itself varies (24 fps by
 * request, less on a machine that cannot keep up).
 *
 * The caller owns the window of frames and calls `observe` once per frame, right
 * after evaluating it; this only counts frames since the last reset (to skip the
 * warm-up, and to wait until the window holds none of it) and the time.
 */
export function createSlowStreakTracker(
  thresholds: BackgroundHealthThresholds = DEFAULT_BACKGROUND_HEALTH_THRESHOLDS,
  now: () => number = () => performance.now()
): SlowStreakTracker {
  let seen = 0
  let since: number | null = null
  let lastAt = 0
  let fired = false

  return {
    get streaking() {
      return since !== null
    },
    observe(action) {
      seen += 1
      // The first window with no warm-up frame in it: before that a verdict is
      // partly about the warm-up.
      if (seen < thresholds.warmupFrames + thresholds.windowSize) return false
      const at = now()
      const gap = at - lastAt
      lastAt = at
      if (action === 'ok' || gap > SLOW_STREAK_MAX_GAP_MS) since = null
      if (action === 'ok') return false
      if (since === null) since = at
      if (fired || at - since < thresholds.sustainedMs) return false
      // Once per tracker: the answer to it is a rebuild with its own tracker, and
      // asking again every frame until then would only repeat it.
      fired = true
      return true
    },
    reset() {
      seen = 0
      since = null
      lastAt = 0
      fired = false
    },
  }
}
